// Aktif Siparişler tablosu için gruplama mantığı.
//
// `delivery/list` her satırda bir DELIVERY (taşıma) döner; bir delivery birden fazla
// `orders[]` içerebilir (mergelenmiş/batch teslimat). Bu dosya delivery'leri sipariş
// bazında düzleştirip önce "Atama Bekliyor" (kuyruk), sonra kurye bazlı gruplara ayırır.

import {calculateDuration} from "utils/helpers";
import {deliveryStatuses} from "constants/constants";

export const resolveStatusKey = (delivery, courierId) => {
    if (courierId) return "assigned"; // Kurye Atandı
    if (delivery?.forceCourier?._id) return "forceAssigned"; // Zorunlu atama yapıldı, kurye henüz almadı
    return "queue"; // Kuyrukta Bekliyor
};

const flattenDeliveries = deliveries => {
    const rows = [];
    (deliveries || []).forEach(delivery => {
        const orders = delivery.orders?.length ? delivery.orders : [{}];
        const courierId = delivery.assignedCourier?._id || null;
        orders.forEach((order, index) => {
            rows.push({
                _rowType: "order",
                key: `${delivery._id}-${order._id || order.orderNumber || index}`,
                deliveryId: delivery._id,
                orderId: order._id,
                orderNumber: order.orderNumber || delivery.orders?.[0]?.orderNumber || "-",
                subCompanyName:
                    order.pickupInfoName ||
                    delivery.pickupInfos?.[0]?.name ||
                    delivery.company?.name ||
                    "-",
                companyName:
                    order.pickupInfoCompanyName ||
                    delivery.company?.companyName ||
                    delivery.company?.name ||
                    "-",
                receiverName: order.receiverName || null,
                createdAt: delivery.createdAt,
                deliveryState: delivery.deliveryState,
                courierId,
                courierName: delivery.assignedCourier?.fullname || null,
                courierType: delivery.assignedCourier?.type || null,
                forceCourier: delivery.forceCourier || null,
                statusKey: resolveStatusKey(delivery, courierId),
                delivery
            });
        });
    });
    return rows;
};

const groupCurrentDeliveriesByCourier = deliveries => {
    const map = new Map();

    (deliveries || []).forEach(delivery => {
        const courierId = delivery.assignedCourier?._id;
        if (!courierId) return; // "current" (aktif) kayıtta kurye atanmamış olmamalı; güvenlik için atla
        if (!map.has(courierId)) {
            map.set(courierId, {
                courierId,
                courierName: delivery.assignedCourier?.fullname || "-",
                courierType: delivery.assignedCourier?.type || null,
                deliveries: []
            });
        }
        map.get(courierId).deliveries.push(delivery);
    });

    return Array.from(map.values()).map(group => {
        // "Siparişi Ayır" ve "Transfer Et" ikonları grup başlığında (tek bir yerde) gösteriliyor.
        // İkisi de tek bir deliveryId gerektirir: kurye tek bir (mergelenmiş ya da tekil) delivery
        // taşıyorsa hedef delivery bellidir; birden fazla AYRI delivery taşıyorsa hangisinin
        // kastedildiği belirsiz olduğundan iki aksiyon da o grupta gizlenir.
        const soleDelivery = group.deliveries.length === 1 ? group.deliveries[0] : null;
        // Kurye grupları listede bu tarihe göre sıralanır (en yeni atama en üstte); kuryenin
        // birden fazla delivery'si varsa en güncel olanı esas alınır.
        const lastOrderCreatedAt = group.deliveries.reduce((latest, delivery) => {
            const createdAt = delivery.createdAt ? new Date(delivery.createdAt).getTime() : 0;
            return createdAt > latest ? createdAt : latest;
        }, 0);

        return {
            ...group,
            orders: flattenDeliveries(group.deliveries),
            count: flattenDeliveries(group.deliveries).length,
            lastOrderCreatedAt,
            transferDeliveryId: soleDelivery?._id || null,
            splitDeliveryId:
                soleDelivery && soleDelivery.orders?.length > 1 ? soleDelivery._id : null,
            splitDelivery: soleDelivery && soleDelivery.orders?.length > 1 ? soleDelivery : null
        };
    });
};

// "queue" ve "forceAssigned" bu ekrana özgü sentetik durumlardır (backend'in ham deliveryState
// alanında karşılığı yok — resolveStatusKey tarafından courierId/forceCourier'a bakılarak
// türetilir). Filtrede bu ikisi row.statusKey ile, geri kalanı row.deliveryState ile eşleşir.
export const SYNTHETIC_STATUS_FILTERS = new Set(["queue", "forceAssigned"]);

// Sentetik durumların ardından, Normal Siparişler ekranının Aktif Siparişler sekmesindeki
// (deliveryStatuses'ta type: "current") durum listesiyle birebir aynı seçenekler eklenir —
// iki ekranda aynı durumlar aynı isimle filtrelensin diye.
export const ORDER_STATUS_FILTERS = [
    {value: "all", label: "Tümü"},
    {value: "queue", label: "Kuyrukta Bekliyor"},
    {value: "forceAssigned", label: "Atama Bekleniyor"},
    ...deliveryStatuses
        .filter(status => status.type === "current")
        .map(status => ({value: status.value, label: status.shortName}))
];

/**
 * Kuyruk + aktif delivery listelerinden tabloya basılacak düz satır dizisini üretir.
 * Her eleman ya bir grup başlığı (`_rowType: "group"`) ya da bir sipariş satırıdır
 * (`_rowType: "order"`). Grup başlıkları antd Table'da colSpan ile tam genişlik render edilir.
 *
 * `statusFilter` verilirse (ve "all" değilse) yalnızca o duruma uyan sipariş satırları kalır;
 * bir grubun tüm satırları elenirse grup başlığı da gösterilmez, kalanların sayısı grup
 * başlığındaki sayaca yansır.
 *
 * `courierSearchText` verilirse kurye adına göre client-side filtre uygulanır — kuryesi olmayan
 * "Atama Bekliyor" grubu (arama dolu iken eşleşecek kurye adı yok) tamamen elenir.
 */
export const buildOrderRows = ({
    queueDeliveries = [],
    currentDeliveries = [],
    statusFilter = "all",
    courierSearchText = ""
}) => {
    let queueOrders = flattenDeliveries(queueDeliveries);
    let courierGroups = groupCurrentDeliveriesByCourier(currentDeliveries);

    if (statusFilter && statusFilter !== "all") {
        const matchesFilter = row =>
            SYNTHETIC_STATUS_FILTERS.has(statusFilter)
                ? row.statusKey === statusFilter
                : row.deliveryState === statusFilter;
        queueOrders = queueOrders.filter(matchesFilter);
        courierGroups = courierGroups
            .map(group => ({
                ...group,
                orders: group.orders.filter(matchesFilter)
            }))
            .filter(group => group.orders.length > 0)
            .map(group => ({...group, count: group.orders.length}));
    }

    const needle = courierSearchText.trim().toLocaleLowerCase("tr-TR");
    if (needle) {
        queueOrders = [];
        courierGroups = courierGroups.filter(group =>
            (group.courierName || "").toLocaleLowerCase("tr-TR").includes(needle)
        );
    }

    courierGroups = courierGroups.sort(
        (a, b) => (b.lastOrderCreatedAt || 0) - (a.lastOrderCreatedAt || 0)
    );

    const rows = [];

    if (queueOrders.length) {
        rows.push({
            _rowType: "group",
            groupType: "queue",
            key: "group-queue",
            count: queueOrders.length
        });
        rows.push(...queueOrders);
    }

    courierGroups.forEach(group => {
        rows.push({
            _rowType: "group",
            groupType: "courier",
            key: `group-courier-${group.courierId}`,
            count: group.count,
            courierId: group.courierId,
            courierName: group.courierName,
            courierType: group.courierType,
            transferDeliveryId: group.transferDeliveryId,
            splitDeliveryId: group.splitDeliveryId,
            splitDelivery: group.splitDelivery
        });
        rows.push(...group.orders);
    });

    return rows;
};

/** Kuyrukta `burningOrdersDuration` (sn) süresinden uzun bekleyen siparişi tespit eder. */
export const isBurningQueueOrder = (row, burningOrdersDurationSeconds) => {
    if (!row?.createdAt) return false;
    const waitedSeconds = (Date.now() - new Date(row.createdAt).getTime()) / 1000;
    return waitedSeconds >= burningOrdersDurationSeconds;
};

/** Kırmızı (burning) kuyruk satırında saatin yanında gösterilen bekleme süresi: "1s 15dk". */
export const formatWaitingDuration = createdAt => {
    if (!createdAt) return "";
    // calculateDuration saniyeyi de basıyor; kolon dar olduğu için saniye kırpılır.
    const text = calculateDuration({startDate: createdAt, endDate: Date.now()})
        .replace(/\d+sn\s*$/, "")
        .trim();
    return text || "<1dk";
};

/** Oluşturulma zamanı tooltip'inde gösterilen açık bekleme süresi: "1 saat 15 dakika". */
export const formatWaitingDurationLong = createdAt => {
    if (!createdAt) return "";
    const text = calculateDuration({
        startDate: createdAt,
        endDate: Date.now(),
        dayUnit: " gün",
        hourUnit: " saat",
        minuteUnit: " dakika",
        secondUnit: " saniye"
    })
        .replace(/\d+ saniye\s*$/, "")
        .trim();
    return text || "1 dakikadan az";
};
